import { useState, useRef, useEffect } from "react";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const Select = ({
    options,
    value,
    setValue,
    defaultText, 
    hasSearch,
    initKey
}: {
    options: { key: string, value: string }[],
    value: string | number,
    setValue: Function,
    defaultText?: string,
    hasSearch?: boolean,
    initKey?: string
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef<HTMLUListElement | null>(null);
    const selectRef = useRef<HTMLDivElement | null>(null);

    // Filter options based on the search term
    const filteredOptions = hasSearch ? options.filter(option =>
        option.key.toLowerCase().includes(searchTerm.toLowerCase())
    ) : options;

    useEffect(() => {
        setSearchTerm(initKey ?? '');
    }, [initKey])

    // Close the dropdown if the click is outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('click', handleClickOutside);
        } else {
            document.removeEventListener('click', handleClickOutside);
        }

        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [isOpen]);

    const handleSelect = (value: string, key: string) => {
        setValue(value);
        if(hasSearch) setSearchTerm(key);
        setIsOpen(false);
    };
    
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
        setIsOpen(true);
    }

    return (
        <div ref={selectRef} style={{ position: "relative", width: "100%" }}>
            <div onClick={() => setIsOpen(!isOpen)} className="select-input">
                {hasSearch ?
                <input
                    type="text"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    placeholder={options.find(option => option.value === value)?.key || defaultText || "Select"}
                    className="select-search-input"
                /> 
                : 
                <span>
                    {options.find(option => option.value === value)?.key || defaultText || "Select"}
                </span>}
                <KeyboardArrowDownIcon />
            </div>
            {isOpen && (
                <div className="select-dropdown-container">
                    <ul ref={dropdownRef} className="select-dropdown">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option, index) => (
                                <li
                                    key={index}
                                    onClick={() => handleSelect(option.value, option.key)}
                                >
                                    {option.key}
                                </li>
                            ))
                        ) : (
                            <li>No options found</li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default Select;